import { Minus, Info } from "lucide-react";
import { Reveal, SectionLabel } from "./primitives";

const COLS = ["LuxAlgo", "LuneFi", "AltFins", "ChartScout", "TradrQuant"];

const ROWS = [
  {
    label: "Price to start",
    cells: ["Free tier + ~$40–120/mo", "~$79/mo subscription", "Free + paid tiers", "Paid tiers", "Free — Pro indicator + alerts"],
  },
  {
    label: "How a setup qualifies",
    cells: ["Preset SMC / signal models", "AI candle score (0–5)", "Screener rules + AI", "Pattern scans", "Confluence 0–6 + Quality 0–100 dual-gate"],
  },
  {
    label: "Logic transparency",
    cells: ["Packaged toolkits", "AI-scored, opaque weighting", "Preset screeners", "Preset scans", "Every input exposed and tunable"],
  },
  {
    label: "Repainting",
    cells: ["Varies per tool", "Locked at signal time", "—", "—", "Confirmed closes only — never repaints"],
  },
  {
    label: "Free real-time alerts",
    cells: ["Paid tiers", "Included with paid plan", "Paid tiers", "Paid tiers", "Free whale + pattern alerts on Telegram"],
  },
  {
    label: "Verified track record",
    cells: ["—", "—", "Aggregate success rates", "Historical ranges", "Per-pattern rate on our own data — full ledger"],
  },
  {
    label: "Beyond the indicator",
    cells: ["Toolkits + screener (paid)", "Screener + VIP Discord (paid)", "Market screening", "Pattern library", "Full terminal: screener, Academy, verified record"],
  },
  {
    label: "Built for",
    cells: ["Broad SMC toolkit users", "AI-signal subscribers", "Market screening", "Pattern scanning", "Retail — a professional-first terminal"],
  },
];

const HONEST = [
  { t: "Where the incumbents shine", d: "LuxAlgo, LuneFi, AltFins and ChartScout are mature, well-built tools. LuxAlgo's Smart-Money toolkit is deep, LuneFi's AI scoring is polished, and AltFins/ChartScout have big screening catalogs. If you want breadth, they deliver." },
  { t: "Where we're different", d: "TradrQuant Pro is free and fully exposed — rule-based, dual-gated logic you can read and tune, not a black-box AI number. And the indicator is one piece of a professional-first terminal built for retail: free alerts, a screener, an Academy, and a verified track record." },
  { t: "Our honest con", d: "We're newer, and each tool is deliberately narrower — we expose the logic instead of stacking dozens of overlays. Our answer: every number we publish is ours, timestamped, and public — wins and losses alike." },
];

const Cell = ({ text, highlight }) => {
  if (text === "—") {
    return (
      <span className="inline-flex items-center gap-1.5 font-term text-[13px] text-white/25">
        <Minus size={13} /> n/a
      </span>
    );
  }
  return (
    <span className={`flex items-start gap-1.5 font-term text-[13px] leading-snug ${highlight ? "text-[#BACD1C]" : "text-white/40"}`}>
      {highlight && <span className="mt-1 h-2 w-2 shrink-0 bg-[#BACD1C]" />}
      {text}
    </span>
  );
};

export const Comparison = () => (
  <section id="compare" data-testid="comparison" className="relative border-t border-white/10 bg-[#0A0A0A]">
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-24 md:py-32">
      <Reveal><SectionLabel index="05">How we compare — indicators & terminals</SectionLabel></Reveal>
      <Reveal delay={0.05}>
        <h2 className="mt-8 font-display font-semibold tracking-[-0.03em] text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl">
          Great tools exist. <span className="text-[#7D8390]">We built the one that fits retail.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.08}>
        <p className="mt-6 max-w-2xl font-term text-sm text-white/55 leading-relaxed">
          LuxAlgo, LuneFi, AltFins and ChartScout are all strong tools — this isn't a takedown.
          It's an honest look at where each fits, and why TradrQuant Pro takes a different path:
          professional-grade, fully-exposed logic, free, inside a terminal built for retail.
        </p>
      </Reveal>

      <Reveal delay={0.1}>
        <p className="mt-8 mb-3 sm:hidden font-term text-[10px] uppercase tracking-[0.25em] text-white/30">// swipe the table →</p>
        <div className="mt-3 sm:mt-12 relative border border-white/12 bg-[#0d0d0d] overflow-x-auto">
          <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1.3fr] min-w-[1080px] border-b border-white/10 bg-[#111111]">
            <span className="px-5 py-4 font-term text-[11px] uppercase tracking-[0.2em] text-white/30">Capability</span>
            {COLS.map((c) => (
              <span
                key={c}
                data-testid={`compare-col-${c.toLowerCase()}`}
                className={`border-l border-white/[0.06] px-5 py-4 font-display font-semibold tracking-[-0.03em] text-sm ${c === "TradrQuant" ? "text-[#ECEBE4] bg-white/[0.03] border-t-2 border-t-[#BACD1C]" : "text-white/40"}`}
              >
                {c === "TradrQuant" && <span className="block font-mono text-[8px] tracking-[0.25em] text-[#BACD1C]/70 mb-1">◆ recommended</span>}
                {c}
              </span>
            ))}
          </div>
          {ROWS.map((r) => (
            <div key={r.label} data-testid="compare-row" className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1.3fr] min-w-[1080px] border-b border-white/[0.06] last:border-b-0 hover:bg-[#111111]/60 transition-colors duration-200">
              <span className="px-5 py-5 font-term text-[13px] text-white/80">{r.label}</span>
              {r.cells.map((cell, i) => (
                <span key={i} className={`border-l border-white/[0.05] px-5 py-5 ${i === r.cells.length - 1 ? "bg-white/[0.03]" : ""}`}>
                  <Cell text={cell} highlight={i === r.cells.length - 1} />
                </span>
              ))}
            </div>
          ))}
        </div>
      </Reveal>

      <div className="mt-8 grid md:grid-cols-3 gap-5">
        {HONEST.map((h, i) => (
          <Reveal key={h.t} delay={0.06 * i}>
            <div className="h-full border border-white/12 bg-[#0d0d0d] p-6">
              <div className="flex items-center gap-2 font-term text-[11px] uppercase tracking-[0.15em] text-[#7D8390]">
                <Info size={13} /> {i === 2 ? "Con" : "Pro"}
              </div>
              <h3 className="mt-4 font-display font-semibold tracking-[-0.03em] text-lg leading-snug">{h.t}</h3>
              <p className="mt-3 font-term text-[13px] text-white/55 leading-relaxed">{h.d}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.12}>
        <p className="mt-8 font-term text-[11px] text-white/30">Competitor details reflect publicly listed pricing/features at time of writing and may change.</p>
      </Reveal>
    </div>
  </section>
);
